import React from 'react'
import {
  AlertMessage,
  Button,
  Dialog,
  MenuItem,
  Select,
  TextField,
  icons,
} from 'cdh-data-portal-theme'
import { useAddGovernorDialog } from './AddGovernorDialog.logic'
import type { AddGovernorOutcome } from './AddGovernorDialog.logic'
import type { useStyles } from '../DataGovernorsPage.style'
import {
  ENVIRONMENT_OPTIONS,
  businessObjectFriendlyNameWithKey,
  hubFriendlyName,
  localText as sharedText,
} from '../DataGovernorsPage.static'
import type { EnvironmentTab } from '../DataGovernorsPage.static'
import { localText } from './AddGovernorDialog.static'

export interface AddGovernorDialogProps {
  readonly classes: ReturnType<typeof useStyles>['classes']
  readonly isOpen: boolean
  readonly activeTab: EnvironmentTab
  readonly defaultHub: string
  readonly defaultBusinessObject: string
  readonly defaultEnvironment: string
  readonly hubOptions: readonly string[]
  readonly businessObjectOptions: readonly string[]
  readonly hubFriendlyNames: Readonly<Record<string, string>>
  readonly onClose: () => void
  readonly onGovernorAdded: (outcome: AddGovernorOutcome) => void
}

export const AddGovernorDialog: React.FC<AddGovernorDialogProps> = ({
  classes,
  isOpen,
  activeTab,
  defaultHub,
  defaultBusinessObject,
  defaultEnvironment,
  hubOptions,
  businessObjectOptions,
  hubFriendlyNames,
  onClose,
  onGovernorAdded,
}) => {
  const { form, setForm, dialogError, isMutationInProgress, closeDialog, submit } = useAddGovernorDialog({
    isOpen,
    activeTab,
    defaultHub,
    defaultBusinessObject,
    defaultEnvironment,
    onClose,
    onGovernorAdded,
  })

  return (
    <Dialog
      actions={
        <div className={classes.dialogActions}>
          <Button
            isDisabled={isMutationInProgress}
            onClick={closeDialog}
            title={sharedText.cancelButton}
            variant="text"
          />
          <Button
            icon={icons.add}
            isLoading={isMutationInProgress}
            onClick={() => void submit()}
            title={sharedText.addGovernorButton}
            variant="primary"
          />
        </div>
      }
      isOpen={isOpen}
      minWidth={0}
      onClose={closeDialog}
      title={sharedText.addGovernorButton}
      width={720}
    >
      <div className={classes.dialogFields}>
        <TextField
          id="governor-email"
          label={localText.emailLabel}
          onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
          placeholder={localText.emailPlaceholder}
          value={form.email}
          withAutoFocus
        />
        <div className={classes.dialogRow}>
          <Select
            label={localText.hubLabel}
            onChange={(event) => setForm((current) => ({ ...current, hub: event.target.value }))}
            placeholder={localText.hubPlaceholder}
            value={form.hub}
          >
            {hubOptions.map((hub) => (
              <MenuItem key={hub} selectable value={hub}>
                {hubFriendlyName(hub, hubFriendlyNames)}
              </MenuItem>
            ))}
          </Select>
          <Select
            label={localText.businessObjectLabel}
            onChange={(event) => setForm((current) => ({ ...current, businessObject: event.target.value }))}
            placeholder={localText.businessObjectPlaceholder}
            value={form.businessObject}
          >
            {businessObjectOptions.map((businessObject) => (
              <MenuItem key={businessObject} selectable value={businessObject}>
                {businessObjectFriendlyNameWithKey(businessObject)}
              </MenuItem>
            ))}
          </Select>
        </div>
        {activeTab === 'int' && (
          <Select
            label={sharedText.environmentLabel}
            onChange={(event) => setForm((current) => ({ ...current, environment: event.target.value }))}
            placeholder={sharedText.environmentLabel}
            value={form.environment}
          >
            {ENVIRONMENT_OPTIONS.map((option) => (
              <MenuItem key={option.value} selectable value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
        {activeTab === 'prod' && (
          <TextField
            id="governor-ticket-id"
            label={localText.ticketUrlLabel}
            onChange={(event) => setForm((current) => ({ ...current, ticketId: event.target.value }))}
            placeholder={localText.ticketUrlPlaceholder}
            value={form.ticketId}
          />
        )}
        {dialogError && (
          <AlertMessage message={dialogError} title={localText.unableToAddGovernorTitle} variant="error" />
        )}
      </div>
    </Dialog>
  )
}
